/**
 * Admin Login Page — /admin/login
 *
 * Email + password login.
 * Password is verified against VITE_ADMIN_PASSWORD env var.
 * Account is verified against Supabase admin_users (is_active = true required).
 * Redirects to originally requested page on success.
 */
import { useState, type FormEvent } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAdminAuth } from "../../../contexts/AdminAuthContext";
import { useAdminT } from "../../../hooks/useAdminT";
import { inputCls } from "../components/AdminUI";

export default function AdminLoginPage() {
  const { login } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useAdminT();

  const from = (location.state as { from?: string })?.from || "/admin";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setError(null);
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      const msg = (err as Error)?.message || "";
      if (msg === "INVALID_CREDENTIALS") {
        setError(t("tabifotoAdmin.auth.login_error_invalid"));
      } else if (msg === "SUPABASE_NOT_CONFIGURED") {
        setError(t("tabifotoAdmin.auth.login_error_supabase"));
      } else {
        setError(t("tabifotoAdmin.auth.login_error_network"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F3] flex flex-col items-center justify-center px-4">
      {/* Card */}
      <div className="w-full max-w-sm bg-white rounded-xl border border-[#D9D9D9]/60 p-8">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#C8B8A6]/20 text-[#C8B8A6] shrink-0">
            <i className="ri-camera-2-line text-base" />
          </div>
          <div>
            <p className="text-sm font-light tracking-widest uppercase text-[#2C2C2C]">TaBiFoTo</p>
            <p className="text-xs text-[#2C2C2C]/40 font-light">{t("tabifotoAdmin.auth.login_subtitle")}</p>
          </div>
        </div>

        <h1 className="text-lg font-light text-[#2C2C2C] mb-6 tracking-wide">
          {t("tabifotoAdmin.auth.login_title")}
        </h1>

        <form onSubmit={handleSubmit} data-readdy-form>
          {/* Email */}
          <div className="mb-4">
            <label className="block text-xs text-[#2C2C2C]/60 font-light mb-1.5 uppercase tracking-wider">
              {t("tabifotoAdmin.auth.email_label")}
            </label>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("tabifotoAdmin.auth.email_placeholder")}
              className={`${inputCls} text-sm`}
              autoComplete="email"
              disabled={loading}
              required
            />
          </div>

          {/* Password */}
          <div className="mb-5">
            <label className="block text-xs text-[#2C2C2C]/60 font-light mb-1.5 uppercase tracking-wider">
              {t("tabifotoAdmin.auth.password_label")}
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("tabifotoAdmin.auth.password_placeholder")}
                className={`${inputCls} text-sm pr-10`}
                autoComplete="current-password"
                disabled={loading}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2C2C2C]/30 hover:text-[#2C2C2C]/60 cursor-pointer"
                tabIndex={-1}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={showPassword ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"} />
                </div>
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg">
              <div className="w-4 h-4 flex items-center justify-center text-red-400 shrink-0 mt-0.5">
                <i className="ri-close-circle-line text-sm" />
              </div>
              <p className="text-xs text-red-600 font-light">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !email.trim() || !password}
            className="w-full px-4 py-2.5 rounded-md bg-[#2C2C2C] text-white text-sm font-light hover:bg-[#2C2C2C]/80 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {loading && (
              <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin shrink-0" />
            )}
            {loading ? t("tabifotoAdmin.auth.logging_in") : t("tabifotoAdmin.auth.login_button")}
          </button>
        </form>
      </div>

      {/* Back link */}
      <Link
        to="/"
        className="mt-5 text-xs text-[#2C2C2C]/30 hover:text-[#2C2C2C]/60 transition-colors cursor-pointer"
      >
        &larr; TaBiFoTo
      </Link>
    </div>
  );
}
